import { Link } from "react-router-dom";
import BrandQR from "../components/BrandQR";

const LAST_UPDATED = "May 1, 2026";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      {/* Nav */}
      <nav className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-heading text-4xl font-semibold tracking-tight mb-2">
          Refund Policy
        </h1>
        <p className="text-sm text-zinc-500 mb-10">Last Updated: {LAST_UPDATED}</p>

        <div className="space-y-8 text-zinc-700 leading-relaxed">

          <p>
            Welcome to <strong>Growithevent</strong> ("we," "our," "us"). This Refund
            Policy outlines the terms related to payments made through our platform:{" "}
            <a
              href="https://www.growithevent.com"
              className="text-zinc-900 underline underline-offset-2"
            >
              www.growithevent.com
            </a>
          </p>
          <p>
            By purchasing a ticket or registering for an event through our platform,
            you agree to this Refund Policy.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              1. Platform Role
            </h2>
            <p className="mb-2">
              Growithevent acts solely as a technology platform that facilitates:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Event listings</li>
              <li>Participant registrations</li>
              <li>Payment processing</li>
              <li>QR-based entry and attendance tracking</li>
            </ul>
            <p className="mt-3">
              We do not organize, manage, or control any event listed on the platform.
              All commercial and operational responsibility rests with the respective
              event organizer.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              2. Refund Responsibility
            </h2>
            <p className="mb-3">
              All payments made for events are collected on behalf of event organizers.{" "}
              <strong>Refunds are the sole responsibility of the event organizer.</strong>
            </p>
            <p className="mb-2">Growithevent:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Does not initiate refunds</li>
              <li>Does not process refund requests directly</li>
              <li>Is not responsible for refund decisions made by organizers</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              3. No Refunds by Growithevent
            </h2>
            <p className="mb-2">
              Growithevent does not provide refunds under any circumstances, including
              but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Change of plans by the participant</li>
              <li>Failure to attend the event</li>
              <li>Dissatisfaction with the event experience</li>
              <li>Event cancellation, postponement, or rescheduling by the organizer</li>
              <li>Technical issues on the participant's device or internet connection</li>
              <li>Duplicate registrations made in error by the participant</li>
            </ul>
            <p className="mt-3">
              All such matters must be addressed <strong>directly with the event organizer</strong>.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              4. Organizer Refund Policies
            </h2>
            <p className="mb-2">
              Each event organizer may define their own refund or cancellation policy
              for their event.
            </p>
            <p className="mb-2">Participants are advised to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Review event details and any stated refund policy before making payment</li>
              <li>
                Contact the event organizer directly for any refund-related queries
              </li>
              <li>Retain their ticket or registration confirmation for reference</li>
            </ul>
            <p className="mt-3">
              Growithevent is not responsible for enforcing or overriding an organizer's
              refund policy.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              5. Payment Processing
            </h2>
            <p className="mb-2">
              Payments are processed through third-party payment providers such as{" "}
              <strong>Cashfree</strong>.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Growithevent does not store full payment or card details</li>
              <li>
                Payment failures or technical errors during checkout should be reported
                to us at{" "}
                <a
                  href="mailto:growithevent@gmail.com"
                  className="text-zinc-900 underline underline-offset-2"
                >
                  growithevent@gmail.com
                </a>{" "}
                for investigation
              </li>
              <li>
                Any payment disputes should be directed to the organizer or the
                respective payment provider
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              6. Chargebacks and Disputes
            </h2>
            <p className="mb-2">In case of payment disputes or chargebacks:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Growithevent reserves the right to provide transaction records and
                relevant data to payment processors or financial institutions as required
              </li>
              <li>
                Users may be required to resolve disputes directly with the event
                organizer
              </li>
              <li>
                Initiating a fraudulent chargeback may result in account suspension and
                legal action
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              7. Exceptional Circumstances
            </h2>
            <p className="mb-2">
              In rare cases, Growithevent may — at its sole and absolute discretion —
              assist in facilitating communication between participants and organizers.
              However:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We are under no obligation to issue or arrange refunds</li>
              <li>Final refund decisions remain solely with the organizer</li>
              <li>
                Any goodwill assistance provided by Growithevent does not create a
                precedent or ongoing obligation
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              8. Platform Fee Non-Refundability
            </h2>
            <p>
              The platform fee charged by Growithevent (3% of the ticket value, which
              together with Razorpay&apos;s 2% processing fee makes up the 5% total fee)
              is <strong>non-refundable</strong> under all circumstances, as it covers
              the cost of infrastructure, payment processing, and platform operations
              regardless of event outcomes.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              9. Changes to This Policy
            </h2>
            <p>
              We may update this Refund Policy at any time. Changes will be posted on
              this page with an updated date. Continued use of the platform after any
              changes constitutes your acceptance of the revised policy.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              10. Contact Us
            </h2>
            <p className="mb-3">
              For any questions or concerns regarding this Refund Policy, please contact
              us:
            </p>
            <ul className="space-y-1">
              <li>
                <span className="text-zinc-500">Email:</span>{" "}
                <a
                  href="mailto:growithevent@gmail.com"
                  className="text-zinc-900 underline underline-offset-2"
                >
                  growithevent@gmail.com
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Address:</span> Gurugram, Haryana,
                India
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="max-w-4xl mx-auto px-6 py-10 text-sm text-zinc-500 border-t border-zinc-200 flex justify-between">
        <span>© Growithevent · MVP build</span>
        <Link to="/" className="hover:text-zinc-900 transition-colors">
          Back to Home
        </Link>
      </footer>
    </div>
  );
}
